import * as React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  login,
  register,
  authCallback,
} from '../data/auth/authActions';
import { LoginLayout } from './static/Layouts';
import {
  FormInput,
  TextField,
} from './static/Inputs';
import { Button } from './static/Elements';
import Icon from './static/Icons';
import Logo from '../media/logo.svg';
import createLogger from '../utils/createLogger';

const DEBUG = true;
const debug = createLogger(DEBUG, `Login.js`);

const ViewMode = {
  LOGIN: 0,
  REGISTER: 1,
};

const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

class Auth extends React.Component {

  static propTypes = {
    authCallback: PropTypes.func.isRequired,
    location: PropTypes.object.isRequired,
    session: PropTypes.object,
    history: PropTypes.object.isRequired,
    register: PropTypes.func.isRequired,
    login: PropTypes.func.isRequired,
  };

  /* ------------ lifecycle methods ------------ */
  constructor(props) {
    super(props);
    debug.enter(`constructor`);

    this.state = {
      view: ViewMode.LOGIN,
      error: null,
      email: ``,
      password: ``,
      confirmPassword: ``,
    }
  }

  componentDidMount() {
    const { redirect } = this;
    const {
      session,
      authCallback,
      location: { hash },
    } = this.props;

    if (!!hash && /access_token|id_token|error/.test(hash))
      authCallback(hash, redirect);
    else if (!!session)
      redirect();
  }

  /* ------------ private helpers ------------ */
  redirect = (error) => {
    const { history } = this.props;

    if (error) {
      this.setState({
        error: { field: `email`, message: error }
      });
    } else {
      history.push(`/jobs`);
    }
  };

  loginHandler = (service) => () => {
    const { redirect } = this;
    const { login } = this.props;
    const {
      email,
      password,
    } = this.state;
    debug.enter(`onSubmitLogin`);

    if (service) {
      debug.log(`  service: ${service}`);
      login({service}, redirect);

    } else {
      debug.log(`  creds:`, { email, password });

      validateFields(
        { email, password },
        (error, creds) => !!error
          ? this.setState({error})
          : login(creds, redirect)
      );
    }
  };

  registerHandler = (service) => () => {
    const { redirect } = this;
    const { register } = this.props;
    const {
      email, password,
      confirmPassword,
    } = this.state;
    debug.enter(`onSubmitRegister`);

    if (service) {
      debug.log(`  service: ${service}`);
      register({service}, redirect);

    } else {
      debug.log(`  creds:`, { email, password });

      validateFields(
        { email, password, confirmPassword },
        (error, creds) => !!error
          ? this.setState({error})
          : register(creds, redirect)
      );
    }
  };

  /* ------------ event handlers ------------ */
  showLogin = () => this.setState({
    view: ViewMode.LOGIN,
    error: null,
    confirmPassword: ``,
    password: ``,
    email: ``,
  });

  showRegister = () => this.setState({
    view: ViewMode.REGISTER,
    error: null,
    confirmPassword: ``,
    password: ``,
    email: ``,
  });

  onFormChange = (field) => (value) => this.setState({[field]: value});


  /* ------------ render methods ------------ */
  renderSignIn = () => {
    const {
      showRegister,
      loginHandler,
      onFormChange,
    } = this;
    const {
      error,
      email,
      password,
    } = this.state;

    const configs = {
      google: {
        type: `auth-google`,
        onClick: loginHandler(`google`),
      },
      email: {
        type: `email`,
        name: `email`,
        value: email,
        placeholder: `Enter email`,
        onChange: onFormChange(`email`),
        validate: (input) => input.match(EMAIL_REGEX) ? `OK` : `Invalid email format`
      },
      password: {
        type: `password`,
        name: `password`,
        value: password,
        placeholder: `Password`,
        onChange: onFormChange(`password`),
      },
    };

    if (error && configs.hasOwnProperty(error.field)) {

      configs[error.field].error = error.message;
    }

    return (
      <LoginLayout>

        <Logo/>

        <Button {...configs.google}>
          <Icon.Google/>
          Sign in with Google
        </Button>

        <p>or, sign in</p>

        <FormInput onSubmit={loginHandler()}>
          <TextField {...configs.email}/>
          <TextField {...configs.password}/>

          <Button primary type="submit">
            Submit
          </Button>
        </FormInput>

        <p> Don't have an account?
          <a onClick={showRegister}> Sign Up.</a>
        </p>

      </LoginLayout>
    );
  };


  renderSignUp = () => {
    const {
      registerHandler,
      onFormChange,
      showLogin,
    } = this;
    const {
      error,
      email,
      password,
      confirmPassword,
    } = this.state;

    const configs = {
      google: {
        type: `auth-google`,
        onClick: registerHandler(`google`),
      },
      email: {
        type: `email`,
        name: `email`,
        value: email,
        placeholder: `Enter email`,
        onChange: onFormChange(`email`),
        validate: (input) => input.match(EMAIL_REGEX) ? `OK` : `Invalid email format`
      },
      password: {
        type: `password`,
        name: `password`,
        value: password,
        placeholder: `Password`,
        onChange: onFormChange(`password`),
      },
      confirmPassword: {
        type: `password`,
        name: `confirmPassword`,
        value: confirmPassword,
        placeholder: `Confirm Password`,
        onChange: onFormChange(`confirmPassword`),
      },
    };

    if (error && configs.hasOwnProperty(error.field)) {
      configs[error.field].error = error.message;
    }

    return (
      <LoginLayout>

        <Logo/>

        <h3>Sign Up</h3>

        <Button {...configs.google}>
          <Icon.Google/>
          Sign up with Google
        </Button>
        <p>or</p>

        <FormInput onSubmit={registerHandler()}>
          <TextField {...configs.email}/>
          <TextField {...configs.password}/>
          <TextField {...configs.confirmPassword}/>

          <Button primary type="submit">
            Submit
          </Button>
        </FormInput>

        <p> Already have an account?
          <a onClick={showLogin}> Sign In.</a>
        </p>

      </LoginLayout>
    );
  };

  render() {
    const { renderSignIn, renderSignUp } = this;
    const { view } = this.state;

    return !view ? renderSignIn() : renderSignUp();
  }
}

const validateFields = ({ email, password, confirmP }, callback ) => {
  if (!email.match(EMAIL_REGEX)) {
    return callback({
      field: `email`,
      message: `Invalid email`,
    });
  }

  if (confirmP !== undefined &&
      password !== confirmP) {
    return callback({
      field: `confirmPassword`,
      message: `Invalid email`,
    });
  }

  return callback(null, { email, password });
};


/* ------------ redux connect ------------ */
const mapStateToProps = (state) => {
  return {
    session: state.auth.session,
  }
};

export default connect(mapStateToProps, {
  authCallback,
  register,
  login,
})(Auth);



// WEBPACK FOOTER //
// ./src/components/Auth.js